import { Col, Row } from "antd";
import to from "await-to-js";
import dayjs from "dayjs";
import { t } from "i18next";
import { omit } from "lodash";
import { useContext, useEffect } from "react";
import { Button } from "../../../../components/Button/Button";
import { ColorText } from "../../../../components/ColorText/ColorText";
import { GoBackButton } from "../../../../components/GoBackButton/GoBackButton";
import { ModalsContext } from "../../../../context/modals.context";
import { MinistryContext } from "../../../../modules/ministry/ministry.context";
import { ministryService } from "../../../../modules/ministry/ministry.service";
import {
  DUVAN_FIELDS_EXCISE_LICENCE,
  DUVAN_FIELDS_EXCISE_WAREHOUSE,
  OTHER_FIELDS_EXCISE_LICENCE,
  OTHER_FIELDS_EXCISE_WAREHOUSE,
} from "../../../../modules/obligee/exciseLicences/exciseLicences.constants";
import { ExciseLicencesContext } from "../../../../modules/obligee/exciseLicences/exciseLicences.context";
import { exciseLicencesService } from "../../../../modules/obligee/exciseLicences/exciseLicences.service";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { toastService } from "../../../../utils/toast.service";
import { Documentation } from "../../../Ministry/components/Documentation/Documentation";
import { InformationAbout } from "../../../Ministry/components/InformationAbout/InformationAbout";
import { Taxes } from "../../../Ministry/ExciseWarehouse/MinistryWarehouseRequestDetails/components/OverviewWarehoseRequest/components/Taxes/Taxes";
import { BottomPart } from "../components/BottomPart/BottomPart";
import { LicenceDetailsCenter } from "./components/LicenceDetailsCenter/LicenceDetailsCenter";
import { LicenceDetailsEditCenter } from "./components/LicenceDetailsEditCenter/LicenceDetailsEditCenter";
import { LicenceDetailsEditTop } from "./components/LicenceDetailsEditTop/LicenceDetailsEditTop";
import { LicenceDetailsTop } from "./components/LicenceDetailsTop/LicenceDetailsTop";
import style from "./LicenceDetails.module.scss";

export const LicenceDetails = () => {
  const {
    exciseLicences,
    setExciseLicences,
    setInformacijeSkladista,
    setInformacijeDozvole,
    clearPayloadState,
  } = useContext(ExciseLicencesContext);
  const { ministry, setMinistry } = useContext(MinistryContext);
  const { setError } = useContext(ModalsContext);

  const updateIzmenaZahtevaDozvole = async () => {
    const dokumentiPayload = exciseLicences.dokumentiZaDozvolu.map(
      (dokument) => {
        return {
          data: dokument.data,
          dozvolaDocumentType: dokument.dozvolaDocumentType,
          format: dokument.format,
        };
      }
    );

    const dozvolaInformacijePayload =
      exciseLicences.dozvolaInformacije.kategorijaProizvodaId ===
      AkciznaMarkicaKategorijaProzivoda.DUVAN
        ? omit(exciseLicences.dozvolaInformacije, OTHER_FIELDS_EXCISE_LICENCE)
        : omit(exciseLicences.dozvolaInformacije, DUVAN_FIELDS_EXCISE_LICENCE);

    const skladisteInformacijePayload =
      exciseLicences.dozvolaInformacije.kategorijaProizvodaId ===
      AkciznaMarkicaKategorijaProzivoda.DUVAN
        ? omit(
            exciseLicences.skladisteInformacijePayload,
            OTHER_FIELDS_EXCISE_WAREHOUSE
          )
        : omit(
            exciseLicences.skladisteInformacijePayload,
            DUVAN_FIELDS_EXCISE_WAREHOUSE
          );

    const params = {
      dozvolaZahtevId: exciseLicences.selectedDozvolaId,
      dokumentiZaDozvolu: dokumentiPayload,
      dozvolaInformacije: dozvolaInformacijePayload,
      skladisteInformacije: skladisteInformacijePayload,
      tipDozvole: exciseLicences.tipDozvole,
    };

    const [err, res] = await to(
      exciseLicencesService.updateIzmenaZahtevaDozvole(params)
    );
    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.IZMENI_LICENCE");
    window.location.reload();
  };

  const createDetaljiZahtevaDozvole = async (id?: number | null) => {
    const [err, res] = await to(
      ministryService.createDetaljiZahtevaDozvole({
        dozvolaZahtevId: Number(id) || exciseLicences.selectedDozvolaId,
        datumResenjaNadleznogOrganaPrikaz: null,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setMinistry({ detaljiODozvoli: res.data });
    const updatedRes = {
      ...res.data.dozvolaInformacije,
      datumResenjaNadleznogOrgana: dayjs(
        res.data.dozvolaInformacije.datumResenjaNadleznogOrgana
      ),
    };
    setExciseLicences({
      dozvolaInformacije: updatedRes,
      skladisteInformacijePayload: res.data.skladisteInformacije,
    });
  };

  let queryParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    clearPayloadState();
    createDetaljiZahtevaDozvole(Number(queryParams.get("id")));

    return () => {
      clearPayloadState();
    };
  }, []);

  const showNotes = () => {
    if (
      ministry.detaljiODozvoli.isVracenNaDopunu ||
      ministry.detaljiODozvoli.isOdbijen
    )
      return (
        <Col span={24}>
          <div className={style.prekidVazenjaNote}>
            {ministry.detaljiODozvoli.statusNotes}
          </div>
        </Col>
      );
  };

  const isReadOnly =
    ministry.detaljiODozvoli.isVracenNaDopunu ||
    ministry.detaljiODozvoli?.isReadonly;

  const createTitle = () => {
    if (["Nov", "Нов"].includes(queryParams.get("tipZahteva") as any))
      return t("AKCIZNE_DOZVOLE.DOBIJANJE");
    if (["Dopuna", "Допуна"].includes(queryParams.get("tipZahteva") as any))
      return t("AKCIZNE_DOZVOLE.IZMENU_RESENJA");
    if (
      ["Obnavljanje", "Обнављање"].includes(
        queryParams.get("tipZahteva") as any
      )
    )
      return t("AKCIZNE_DOZVOLE.OBNJAVLJANJE");
    if (["Prekid", "Прекид"].includes(queryParams.get("tipZahteva") as any))
      return t("AKCIZNE_DOZVOLE.PRESTANAK");
  };

  return (
    <Row className={style.mainContainer}>
      <GoBackButton
        routePath="/obveznik/akcizneDozvole/listaDozvola"
        options={{ state: { usePersistedFilters: true } }}
      />
      <Col className={style.mainTitle} span={24}>
        <h2 className={style.fontSize}>
          {t("AKCIZNE_DOZVOLE.ZAHTEV_ZA")} {createTitle()}{" "}
          {t("AKCIZNE_DOZVOLE.AKCIZNE_DOZVOLE")} {queryParams.get("id")}
        </h2>
        <h3 className={style.title}>
          {ColorText(ministry.detaljiODozvoli.statusZahteva)}
        </h3>
      </Col>
      {ministry.detaljiODozvoli.showPrekidVazenjaNote && (
        <Col span={24}>
          <div className={style.prekidVazenjaNote}>
            {ministry.detaljiODozvoli.prekidVazenjaNote}
          </div>
        </Col>
      )}
      {showNotes()}
      <div className={style.contentWrapper}>
        <div className={style.evidencioniBroj}>
          {t("AKCIZNE_MARKICE.EVIDENCIONI_BROJ")}:{" "}
          {ministry.detaljiODozvoli.evidencioniBroj}
        </div>
        <InformationAbout
          type={t("AKCIZNE_DOZVOLE.PODNOSILAC_ZAHTEVA")}
          data={ministry.detaljiODozvoli.drustvoInformacije}
        />
        {!isReadOnly ? (
          <LicenceDetailsTop
            dataLicenceProduct={
              ministry.detaljiODozvoli.odabranaKategorijaProizvoda
            }
            tipDozvoleId={ministry.detaljiODozvoli.tipDozvole}
            dataLicence={ministry.detaljiODozvoli.dozvolaInformacije}
          />
        ) : (
          <LicenceDetailsEditTop
            setInformacijeDozvole={setInformacijeDozvole}
            tipDozvoleId={ministry.detaljiODozvoli.tipDozvole}
            stateValues={exciseLicences.dozvolaInformacije as any}
          />
        )}
        {!isReadOnly ? (
          <LicenceDetailsCenter
            kategorijaProizvodaId={
              ministry.detaljiODozvoli?.dozvolaInformacije
                ?.kategorijaProizvodaId
            }
            dataLicence={ministry.detaljiODozvoli.skladisteInformacije}
          />
        ) : (
          <LicenceDetailsEditCenter
            meraSelect={ministry.detaljiODozvoli.mereVelicine}
            setInformacijeSkladista={setInformacijeSkladista}
            stateValues={exciseLicences.skladisteInformacijePayload}
            kategorijaProizvodaId={
              exciseLicences.dozvolaInformacije?.kategorijaProizvodaId
            }
          />
        )}
        {!isReadOnly ? (
          <Documentation
            data={ministry.detaljiODozvoli.dokumentiZaDozvolu}
            kategorijaProizvodaId={
              ministry.detaljiODozvoli?.dozvolaInformacije
                ?.kategorijaProizvodaId ??
              exciseLicences?.dozvolaInformacije?.kategorijaProizvodaId
            }
          />
        ) : (
          <BottomPart data={ministry.detaljiODozvoli.dokumentiZaDozvolu} />
        )}
        {(ministry.detaljiODozvoli.dokumentiIzPoreske?.length ?? 0) > 0 && (
          <Taxes
            dozvolaZahtevId={ministry.detaljiODozvoli.dozvolaZahtevId}
            dokumentiPoreska={ministry.detaljiODozvoli.dokumentiIzPoreske}
          />
        )}
      </div>
      {!isReadOnly ? null : (
        <Col className={style.buttonCol} span={24}>
          <Button onClick={updateIzmenaZahtevaDozvole} type="primary">
            {t("AKCIZNE_DOZVOLE.IZMENI")}
          </Button>
        </Col>
      )}
    </Row>
  );
};
